import axios from 'axios';
import {
  markResetCodeVerified,
  forgetResetLink,
} from '../utils/resetLinkSession';

// Plain async function, not a thunk -- this screen has no Redux state of
// its own (it never learns the real email, only a masked one it shows
// locally) so there's nothing to dispatch.
export const findAccount = async (identifier) => {
  const config = {
    headers: {
      'Content-type': 'application/json',
    },
  };
  const body = JSON.stringify({ identifier });

  try {
    const res = await axios.post('/api/users/find-account', body, config);
    return { ok: true, maskedEmail: res.data.maskedEmail };
  } catch (err) {
    const data = err.response && err.response.data;
    const msg = (data && data.msg) || 'Something went wrong. Please try again.';
    return { ok: false, msg };
  }
};

// Seeds the email the reset-code screen needs when the customer arrived
// from the emailed link instead of the request form.
//
// Deliberately makes NO network call. The obvious alternative -- calling
// sendCodeToEmail to populate the same state -- would issue a brand new
// code and spend one of the three requests allowed per two hours, so
// simply opening the link would eat a third of the customer's budget and
// invalidate the very code the link carries. This only moves what the
// link already contains into Redux.
export const openResetLink = (email) => (dispatch) => {
  dispatch({ type: 'RESET_LINK_OPENED', payload: { email } });
};

export const matchCode = (code, email) => async (disptach) => {
  const config = {
    headers: {
      'Content-type': 'application/json',
    },
  };
  const body = JSON.stringify({ code, email });

  try {
    await axios.post('/api/users/code_check', body, config);
    // Remembered for the same reason EnterCode remembers the link: a
    // service-worker reload between here and /new_password would
    // otherwise wipe codeMatched and bounce the customer to /login.
    markResetCodeVerified();
    disptach({ type: 'CODE_MATCHED', payload: code });
  } catch (err) {
    disptach({
      type: 'ERROR',
      payload: { msg: err.response.data.msg },
    });
    setTimeout(() => {
      disptach({
        type: 'CLEAR_ERROR',
      });
    }, 0);
  }
};

export const resetPassword = (password, user, code) => async (disptach) => {
  const config = {
    headers: {
      'Content-type': 'application/json',
    },
  };
  const body = JSON.stringify({ password, email: user, code });

  try {
    await axios.post('/api/users/reset_password', body, config);
    // The flow is over and the code is spent -- drop it rather than
    // leaving it in the tab's storage until the tab closes.
    forgetResetLink();
    disptach({ type: 'PASS_RESET_COMPLETE' });
  } catch (err) {
    disptach({
      type: 'ERROR',
      payload: { msg: err.response.data.msg },
    });
    setTimeout(() => {
      disptach({
        type: 'CLEAR_ERROR',
      });
    }, 0);
  }
};

export const sendCodeToEmail = (email) => async (dispatch) => {
  try {
    const res = await axios.get(`/api/users/reset/${email}`);
    dispatch({
      type: 'EMAIL_FOUND',
      payload: {
        email,
        expiresAt: res.data.expiresAt,
        canResendAt: res.data.canResendAt,
      },
    });
    return {
      ok: true,
      expiresAt: res.data.expiresAt,
      canResendAt: res.data.canResendAt,
    };
  } catch (err) {
    const data = err.response && err.response.data;
    const msg = (data && data.msg) || 'Something went wrong. Please try again.';
    dispatch({
      type: 'ERROR',
      payload: { msg },
    });
    setTimeout(() => {
      dispatch({
        type: 'CLEAR_ERROR',
      });
    }, 0);
    return {
      ok: false,
      msg,
      canResendAt: data && data.canResendAt,
    };
  }
};
